import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@env/environment';
import { FileSaverService } from 'ngx-filesaver';
import { firstValueFrom } from 'rxjs';

@Injectable({
	providedIn: 'root'
})
export class CustomiserApiService {

	private http = inject(HttpClient);
	
	// This is used for downloading files from the API using default http interceptors
	// which will make authentication work the same way as other api requests.
	private fileSaver = inject(FileSaverService);

	public baseUrl = environment.dataUrl + '/v2/';

	public async get<T>(endpoint: string) {
		let result = await firstValueFrom(this.http.get<T>(this.baseUrl + endpoint));

		return result;
	}
	public async getWithQuery<T>(endpoint: string) {
		return await firstValueFrom(this.http.get<T>(this.baseUrl + endpoint));
	}

	public async post<T>(endpoint: string, payload: string) {

		return await firstValueFrom(this.http.post<T>(this.baseUrl + endpoint, payload,));
	}

	public async postJson<T>(endpoint: string, payload: any) {
		return await firstValueFrom(this.http.post<T>(this.baseUrl + endpoint, payload,
			{
				headers: new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8')
			}));
	}
	public async patch<T>(endpoint: string, payload: any) {
		return await firstValueFrom(this.http.patch<T>(this.baseUrl + endpoint, payload));
	}

	public async delete<T>(endpoint: string) {
		let result = await firstValueFrom(this.http.delete<T>(this.baseUrl + endpoint));

		return result;
	}

	public async download(endpoint: string, fallbackFilename = 'download') {
		const request = this.http.get(this.baseUrl + endpoint, {
			observe: 'response',
			responseType: 'blob'
		});

		const response = await firstValueFrom(request);
		const filename = this.parseFilenameFromContentDisposition(response.headers.get('content-disposition'));

		this.fileSaver.save(response.body, filename ?? fallbackFilename);
	}

	/**
	 * Extracts the filename from a Content-Disposition header.
	 * Supports both RFC 5987 encoded filenames (filename*) and standard filename= values.
	 * 
	 * Heavily inspired/copied from Microsoft 365 Copilot
	 */
	private parseFilenameFromContentDisposition(contentDisposition?: string | null): string | null {
		if (!contentDisposition) {
			return null;
		}

		// Matches RFC 5987 encoded filenames: filename*=charset'lang'encodedValue
		const filenameStarMatch = /filename\*\s*=\s*([^']*)'[^']*'([^;]+)/i.exec(contentDisposition);
		if (filenameStarMatch && filenameStarMatch[2]) {
			return decodeURIComponent(filenameStarMatch[2].trim());
		}

		// Matches plain filename= values, with or without surrounding quotes
		const filenameMatch = /filename\s*=\s*\"?([^\";]+)\"?/i.exec(contentDisposition);
		if (filenameMatch && filenameMatch[1]) {
			return filenameMatch[1].trim();
		}

		return null;
	}

}
